// B2BQuickReorder — top-5 SKUs from buyer's last 3 orders with one-tap add-to-cart.
import React, { useEffect, useState } from "react";
import { Plus, ShoppingBasket, Minus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";
import { useTranslation } from "react-i18next";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const B2BQuickReorder = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState({}); // { product_id: qty }
    const [busy, setBusy] = useState({}); // { product_id: bool }

    useEffect(() => {
        api.get("/b2b/quick-reorder").then((r) => {
            setData(r.data);
            const initial = {};
            (r.data.items || []).forEach((it) => { initial[it.product_id] = 1; });
            setQty(initial);
        }).catch(() => setData({ items: [], ready: false })).finally(() => setLoading(false));
    }, []);

    const updateQty = (pid, delta) => {
        setQty((prev) => {
            const next = Math.max(1, (prev[pid] || 1) + delta);
            return { ...prev, [pid]: next };
        });
    };

    const addToCart = async (item) => {
        const q = qty[item.product_id] || 1;
        setBusy((b) => ({ ...b, [item.product_id]: true }));
        try {
            await api.post("/b2b/cart/items", { product_id: item.product_id, qty: q });
            toast.success(`${item.name} × ${q} ${t("added to cart")}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || t("Could not add to cart"));
        } finally {
            setBusy((b) => ({ ...b, [item.product_id]: false }));
        }
    };

    if (loading) {
        return <div className="h-44 skeleton rounded-2xl" data-testid="b2b-quick-reorder-loading" />;
    }

    if (!data?.ready) {
        const need = (data?.needed || 3) - (data?.orders_so_far || 0);
        return (
            <section className="rounded-2xl border border-dashed border-brand-200 bg-brand-50/30 p-5" data-testid="b2b-quick-reorder-empty">
                <div className="flex items-center gap-3">
                    <ShoppingBasket className="h-5 w-5 text-brand-700" />
                    <div>
                        <p className="label-uppercase">{t("Quick reorder")}</p>
                        <p className="mt-1 text-sm font-medium text-zinc-900">{t("Personalised top 5 from your last 3 orders")}</p>
                        <p className="text-xs text-zinc-600">
                            {data?.orders_so_far === 0
                                ? t("Order a few products to get personalised quick-reorder suggestions.")
                                : t("Place {{n}} more order(s) to unlock quick reorder.").replace("{{n}}", String(need))}
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="b2b-quick-reorder">
            <div className="mb-3 flex items-baseline justify-between">
                <div>
                    <p className="label-uppercase">{t("Quick reorder")}</p>
                    <h3 className="mt-1 text-base font-semibold tracking-tight text-zinc-900">{t("Your top 5 in 1 tap")}</h3>
                </div>
                <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-800">
                    {data.tier_discount_pct}% {t("off")}
                </span>
            </div>
            <div className="-mx-1 flex gap-3 overflow-x-auto pb-2" data-testid="b2b-quick-reorder-row">
                {data.items.map((it) => {
                    const q = qty[it.product_id] || 1;
                    const isBusy = busy[it.product_id];
                    const oos = it.stock_qty <= 0;
                    return (
                        <article
                            key={it.product_id}
                            className="flex w-52 shrink-0 flex-col gap-2 rounded-xl border border-zinc-100 bg-white p-3 transition-shadow hover:shadow-md"
                            data-testid={`b2b-quick-reorder-item-${it.product_id}`}
                        >
                            <div className="aspect-square overflow-hidden rounded-lg bg-zinc-50">
                                {it.images?.[0] ? (
                                    <img src={it.images[0]} alt={it.name} className="h-full w-full object-cover" />
                                ) : (
                                    <div className="grid h-full w-full place-items-center text-zinc-300">
                                        <ShoppingBasket className="h-8 w-8" />
                                    </div>
                                )}
                            </div>
                            <h4 className="line-clamp-2 text-xs font-medium text-zinc-900">{it.name}</h4>
                            <p className="text-[10px] text-zinc-500">{it.brand} · {it.pack_size}</p>
                            <div className="flex items-baseline gap-1">
                                <span className="text-sm font-bold text-zinc-900">{inr(it.wholesale_price)}</span>
                                {it.mrp > it.wholesale_price && (
                                    <span className="text-[10px] text-zinc-400 line-through">{inr(it.mrp)}</span>
                                )}
                            </div>
                            <p className="text-[10px] text-emerald-700">{t("Last 3 orders")}: {it.total_qty_ordered}</p>
                            {oos ? (
                                <span className="rounded-md bg-zinc-100 px-2 py-1.5 text-center text-[10px] font-medium text-zinc-500">{t("Out of stock")}</span>
                            ) : it.prescription_required ? (
                                <span className="rounded-md bg-amber-50 px-2 py-1.5 text-center text-[10px] font-medium text-amber-700">{t("Rx required")}</span>
                            ) : (
                                <div className="flex items-center gap-1.5">
                                    <div className="flex items-center rounded-md border border-zinc-200 overflow-hidden">
                                        <button onClick={() => updateQty(it.product_id, -1)} className="p-1 hover:bg-zinc-50" data-testid={`b2b-qr-dec-${it.product_id}`}>
                                            <Minus className="h-3 w-3" />
                                        </button>
                                        <span className="px-2 text-xs font-medium tabular-nums" data-testid={`b2b-qr-qty-${it.product_id}`}>{q}</span>
                                        <button onClick={() => updateQty(it.product_id, +1)} className="p-1 hover:bg-zinc-50" data-testid={`b2b-qr-inc-${it.product_id}`}>
                                            <Plus className="h-3 w-3" />
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => addToCart(it)}
                                        disabled={isBusy}
                                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-brand-700 px-2 py-1.5 text-[11px] font-medium text-white hover:bg-brand-800 disabled:opacity-50"
                                        data-testid={`b2b-qr-add-${it.product_id}`}
                                    >
                                        {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                                        {t("Add")}
                                    </button>
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

export default B2BQuickReorder;
